import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Sidebar } from './components/layout/Sidebar/Sidebar';
import { useUIStore } from './stores/uiStore';
import { ModalManager } from './components/ModalManager';
import { LinkHost } from './components/LinkHost';

// Pages
import { HomePage } from './pages/HomePage/HomePage';
import { RoomControlsPage } from './pages/RoomControlsPage/RoomControlsPage';
import { RoomGuidePage } from './pages/RoomGuidePage/RoomGuidePage';
import { DineInPage } from './pages/DineInPage/DineInPage';
import { PropertyInfoPage } from './pages/PropertyInfoPage/PropertyInfoPage';
import { OffersPage } from './pages/OffersPage/OffersPage';
import { SettingsPage } from './pages/SettingsPage/SettingsPage';
import { TechnicianPage } from './pages/TechnicianPage/TechnicianPage';
import { ROUTE } from './lib/rcu/catalog/navigation';

// Page transition wrapper
const PageTransition = ({ children }: { children: React.ReactNode }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  );
};

// App Content with routing
const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarExpanded, toggleSidebar } = useUIStore();

  // The technician console is a hidden route: no guest chrome around it.
  const isTechnicianRoute = location.pathname === ROUTE.TECHNICIAN;

  return (
    <div className="flex h-screen overflow-hidden bg-[#FAFAFA]">
      {/* One broker session for the whole app, guest pages included. */}
      <LinkHost />
      {!isTechnicianRoute && (
        <Sidebar
          activeRoute={location.pathname}
          onNavigate={(route) => navigate(route)}
          expanded={sidebarExpanded}
          onToggleExpand={toggleSidebar}
        />
      )}
      <main className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route
              path="/"
              element={
                <PageTransition>
                  <HomePage />
                </PageTransition>
              }
            />
            <Route
              path={ROUTE.CONTROLS}
              element={
                <PageTransition>
                  <RoomControlsPage />
                </PageTransition>
              }
            />
            <Route
              path={ROUTE.TECHNICIAN}
              element={
                <PageTransition>
                  <TechnicianPage />
                </PageTransition>
              }
            />
            <Route
              path="/guide"
              element={
                <PageTransition>
                  <RoomGuidePage />
                </PageTransition>
              }
            />
            <Route
              path="/dine"
              element={
                <PageTransition>
                  <DineInPage />
                </PageTransition>
              }
            />
            <Route
              path="/property"
              element={
                <PageTransition>
                  <PropertyInfoPage />
                </PageTransition>
              }
            />
            <Route
              path="/offers"
              element={
                <PageTransition>
                  <OffersPage />
                </PageTransition>
              }
            />
            <Route
              path="/settings"
              element={
                <PageTransition>
                  <SettingsPage />
                </PageTransition>
              }
            />
          </Routes>
        </AnimatePresence>
      </main>
      <ModalManager />
    </div>
  );
};

// basename follows the deploy root: "/" in dev, "/<repo>/" on GitHub Pages.
function App() {
  return (
    <Router basename={import.meta.env.BASE_URL}>
      <AppContent />
    </Router>
  );
}

export default App;
